import type { RadioChangeEvent } from 'antd';
import useSegmentTracking from 'core/utils/segment/useSegmentTracking';
import DisplayTrapNameId from '@features/regional-overview/digital-traps/components/display-trap-name-id';
import { useDigitalTrapsStore } from '@features/regional-overview/digital-traps/digital-traps.zustand';
import type { TrapGroup } from '@features/regional-overview/digital-traps/domain';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFlightCurveStore } from '../flight-curve.zustand';
import { ALL_TRAPS_SELECTED_VALUE, ViewMode } from './sidebar.model';

export const useSidebar = () => {
  const [t] = useTranslation();
  const trapGroups = useDigitalTrapsStore(state => state.trapGroups);
  const { selectedTraps: selectedTrapsStore, selectedNetwork, setSelectedNetwork, setSelectedTraps } = useFlightCurveStore();
  const segmentTracking = useSegmentTracking();
  const [viewMode, setViewMode] = useState(ViewMode.TOTAL_COUNTS);

  useEffect(() => {
    setSelectedNetwork(trapGroups[0]);
  }, [trapGroups, setSelectedNetwork]);

  const shouldDisableTrapOption = useCallback(
    (id: string) => {
      if (selectedTrapsStore.length >= 4) {
        const isTrapSelected = selectedTrapsStore.includes(id);
        return !isTrapSelected;
      }
      return false;
    },
    [selectedTrapsStore]
  );

  const trapOptions = useMemo(() => {
    const network = trapGroups.find(group => group.id === selectedNetwork?.id);
    return network?.trapsInGroup.map(feature => {
      const { id, name } = feature.properties;
      return {
        value: id,
        label: <DisplayTrapNameId trapName={name} trapId={id} />,
        disabled: shouldDisableTrapOption(id)
      };
    });
  }, [trapGroups, selectedNetwork, shouldDisableTrapOption]);

  const selectedTraps = useMemo(() => {
    if (selectedTrapsStore[0] === ALL_TRAPS_SELECTED_VALUE) return [];

    return selectedTrapsStore;
  }, [selectedTrapsStore]);

  const viewModeOptions = [
    { value: ViewMode.TOTAL_COUNTS, label: t('pages.regional_overview.digital_traps.total_network_counts') },
    { value: ViewMode.COMPARE_TRAPS, label: t('pages.regional_overview.digital_traps.compare_individual_traps') }
  ];

  const handleViewModeChange = (e: RadioChangeEvent) => {
    setViewMode(e.target.value as ViewMode);

    if (e.target.value === ViewMode.TOTAL_COUNTS) {
      setSelectedTraps([ALL_TRAPS_SELECTED_VALUE]);
    } else {
      setSelectedTraps([]);
    }

    segmentTracking.track('Digital Traps - Flight Curve - View Mode', {
      viewMode: e.target.value
    });
  };

  const handleNetworkSelect = (_networkId: string, selectedOption: TrapGroup) => {
    setSelectedNetwork(selectedOption);
    setViewMode(ViewMode.TOTAL_COUNTS);
    setSelectedTraps([ALL_TRAPS_SELECTED_VALUE]);

    segmentTracking.track('Digital Traps - Flight Curve - Selected Network', {
      networkName: selectedOption.title,
      viewMode
    });
  };

  const handleTrapSelect = (trapIds: string[]) => {
    setSelectedTraps(trapIds);

    segmentTracking.track('Digital Traps - Flight Curve - Selected Traps', {
      trapIds,
      viewMode
    });
  };

  return {
    trapOptions,
    selectedTraps,
    setSelectedTraps,
    viewModeOptions,
    handleViewModeChange,
    viewMode,
    selectedNetwork,
    handleNetworkSelect,
    handleTrapSelect,
    t
  };
};
